import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "api";
import { download, debug } from "utils";

import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "components/stripe/StripePaymentForm";

export default function PageOrderPay() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [invoiceToken, setInvoiceToken] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState();
  const [error, setError] = useState(null);
  // -----------------------------
  // Chargement du lien de paiement
  // -----------------------------
  useEffect(() => {
    if (!token) return;
    const loadPaymentData = async () => {
      try {
        setLoading(true);
        setError(null);
        setInvoiceToken(null);
        setPaymentData(null);
        api.handleError = false;
        const res = await api.get(`/orders/payment-link/${token}`);
        const data = res?.data;
        setInvoiceToken(data.orderDownloadToken);
        setPaymentStatus(data.orderPaymentStatus);
        // -----------------------------
        // Cas déjà payé
        // -----------------------------
        if (data?.orderBalance <= 0) {
          if (data.invoiceAvailable && data.orderDownloadToken) {
            setPaymentData({ orderId: data.orderId });

            return;
          }
          return;
        }
        // -----------------------------
        // Cas normal (paiement à faire)
        // -----------------------------
        const paymentData = data?.paymentData;
        if (!paymentData) {
          setError("Lien de paiement invalide.");
          return;
        }
        setPaymentData(paymentData);
        setStripePromise(loadStripe(paymentData.publicKey));

      } catch (e) {
        setError(
          e.response?.data?.message ||
          "Impossible de charger le lien de paiement."
        );
      } finally {
        setLoading(false);
      }
    };

    loadPaymentData();
  }, [token]);
  // -----------------------------
  // Polling paiement
  // -----------------------------
  const checkPaymentStatus = async (orderId, maxRetries = 10) => {
    for (let i = 0; i < maxRetries; i++) {
      api.handleError = false;
      const res = await api.get(`/order/${orderId}/checkPayment`);
      const data = res.data;

      if (data.orderPaymentStatus !== "pending") {
        return data;
      }
      await new Promise(r => setTimeout(r, 1500));
    }
    return { orderPaymentStatus: "pending" };
  };
  // -----------------------------
  // Callback Stripe
  // -----------------------------
  const handleStripeResult = async (status) => {
    if (status !== "success" || !paymentData?.orderId) return;
    Swal.fire({
      title: "Validation du paiement…",
      text: "Confirmation en cours.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });
    try {
      const result = await checkPaymentStatus(paymentData.orderId);
      Swal.close();
      const {
        orderPaymentStatus,
        orderBalance,
        invoiceAvailable,
        orderDownloadToken
      } = result;
      setPaymentStatus(orderPaymentStatus);
      if (orderPaymentStatus === "pending") {
        Swal.fire({
          title: "Paiement validé",
          text: "La confirmation est en cours. Vous recevrez un email dès validation."
        });
        return;
      }
      if (orderPaymentStatus === "partial") {
        const formatted = new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR"
        }).format(orderBalance);
        Swal.fire({
          title: "Paiement partiel confirmé",
          html: `Reste à payer : <strong>${formatted}</strong>`
        });
        return;
      }
      if (orderPaymentStatus === "complete") {
        if (invoiceAvailable && orderDownloadToken) {
          setInvoiceToken(orderDownloadToken);
        } else {
          Swal.fire({
            title: "Paiement confirmé",
            text: "Votre paiement est validé."
          });
        }
        return;
      }
      Swal.fire({
        title: "Statut inconnu",
        text: "Impossible de déterminer l'état du paiement."
      });
    } catch (e) {
      Swal.fire({
        title: "Erreur",
        text: "Impossible de vérifier l'état du paiement."
      });
    }
  };
  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="container py-5" style={{ maxWidth: 600 }}>




      <div className="card shadow-sm">
        <div className="card-body">
          <div className="mb-5 d-flex flex-column gap-3 justify-content-between">
            <h4 className="m-0 p-0">Règlement sécurisé de facture</h4>

          </div>
          {loading && (
            <div className="text-center py-4">
              Chargement des informations…
            </div>
          )}
          {!loading && error && (
            <div className="alert alert-danger small">
              {error}
            </div>
          )}
          {!loading && !error && paymentData && stripePromise && (
            <>
              <p className="text-muted">
                {paymentData.orderLabel}
              </p>
              <div className="mb-4">
                <p>
                  Montant :{" "}
                  <strong>{paymentData.orderBalance} €</strong>
                </p>
                <a
                  className="text-primary decoration-on-hover"
                  onClick={() =>
                    download(
                      `/orders/${paymentData.orderId}/invoice/download?token=${invoiceToken}`
                    )
                  }
                >
                  Afficher le détail des sommes dues
                </a>
              </div>
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: paymentData.clientSecret }}
              >
                <StripePaymentForm onResult={handleStripeResult} />
              </Elements>
            </>
          )}
          {paymentStatus === 'complete' && (
            <div className="mt-4 text-center pb-3">
              <div className="alert alert-success small mb-5">
                <p> Votre commande a été réglée.</p>
                <p> Vous pouvez télécharger votre facture ci-dessous.</p>
              </div>
              <button
                className="btn btn-sm btn-outline-primary rounded-pill"
                onClick={() =>
                  download(
                    `/orders/${paymentData.orderId}/invoice/download?token=${invoiceToken}`
                  )
                }
              >
                Télécharger ma facture
              </button>

            </div>
          )}
        </div>
      </div>

    </div>
  );
}
